import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { HeaderCell, HeaderRow, Icon, PaginatedList, Section, SortLabel, Table, TableBody, TableHead, Text } from 'components';
import { theme, toRem } from 'design';
import { useQueryString } from 'hooks';
import { PayoutReview } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { getSiteReviewPayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections/Payouts';
import { selectCurrentEmployeeSite } from 'store/Employers';
import { IStore } from 'store/types';
import { IPayout, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { IconContainer, ReviewRow } from './styles';

const EmptyTable = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  margin-top: ${toRem(12)};

  p {
    font-size: ${toRem(13.75)};
    color: ${theme.color.neutral[500]};
  }
`;

const ReviewPayments = () => {
  const dispatch = useDispatch();
  const [urlQuery, setUrlQuery] = useQueryString('reviewPayments', window.location, { sort: '-created_at', limit: 10 });

  const currentEmployeeId = useSelector(selectCurrentEmployee())?.id;
  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId || '';
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const reviewPermission = hasSitePermission(currentSite?.permissions, PermissionType.PAYMENT_APPROVAL);

  const reviewPayouts = useSelector(selectPayouts());
  const reviewPayoutsTotal = useSelector(selectTotalCountPayouts());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_REVIEW_PAYOUTS_REQUEST));
  const [selectedPayout, setSelectedPayout] = useState<IPayout>();

  useEffect(() => {
    if (currentSiteId && reviewPermission) {
      dispatch(getSiteReviewPayoutsRequest(currentSiteId, urlQuery));
    }
  }, [dispatch, currentSiteId, reviewPermission, urlQuery]);

  const [showReviewModal, hideReviewModal] = useModal(() => {
    const fetchReviewPayouts = () =>
      dispatch(
        getSiteReviewPayoutsRequest(currentSiteId, {
          sort: urlQuery.sort,
          limit: urlQuery.limit,
        })
      );
    return <PayoutReview payout={selectedPayout} onClose={hideReviewModal} refreshPayouts={fetchReviewPayouts} />;
  }, [selectedPayout, currentSiteId, urlQuery]);

  const onReviewPayout = (payout: IPayout) => {
    setSelectedPayout(payout);
    showReviewModal();
  };

  const onQueryChange = (options: IApiQueryOptions) => {
    setUrlQuery(options);
  };

  const isSorted = (headerValue: string) => urlQuery.sort === headerValue || urlQuery.sort === `-${headerValue}`;
  const isSortDesc = urlQuery.sort?.[0] === '-';

  const onSortChange = (newSort: string) => {
    setUrlQuery({ sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort });
  };

  return (
    <>
      {!isLoading && reviewPayoutsTotal === 0 ? (
        <EmptyTable>
          <IconContainer>
            <Icon icon="CheckmarkThick" fill={theme.color.white} width={45} height={45} />
          </IconContainer>
          <Text>All payments have been reviewed</Text>
        </EmptyTable>
      ) : (
        <Section data-cy="table">
          <PaginatedList total={reviewPayoutsTotal} limit={urlQuery?.limit} offset={urlQuery?.offset} onPageChange={onQueryChange} isLoading={isLoading || !reviewPayoutsTotal} standalone={false}>
            <Table scrollable>
              <TableHead>
                <HeaderRow sticky>
                  <HeaderCell onClick={() => onSortChange('id')} data-cy="header-payment-id">
                    <SortLabel label="Payment ID" isSorted={isSorted('id')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                  </HeaderCell>

                  <HeaderCell onClick={() => onSortChange('employee.name')} data-cy="header-payee">
                    <SortLabel label="Payee" isSorted={isSorted('employee.name')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                  </HeaderCell>

                  <HeaderCell>{isLoading ? '' : 'Sent By'}</HeaderCell>

                  <HeaderCell onClick={() => onSortChange('created_at')} data-cy="header-date">
                    <SortLabel label="Date" isSorted={isSorted('created_at')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                  </HeaderCell>

                  <HeaderCell>{isLoading ? '' : 'Reason'}</HeaderCell>

                  <HeaderCell onClick={() => onSortChange('gross')} data-cy="header-amount">
                    <SortLabel label="Amount" isSorted={isSorted('gross')} isSortedDesc={isSortDesc} isLoading={isLoading} />
                  </HeaderCell>

                  <HeaderCell />
                </HeaderRow>
              </TableHead>

              <TableBody>
                {isLoading || !reviewPayouts ? (
                  <ReviewRow isLoading />
                ) : (
                  reviewPayouts.map((payout) => <ReviewRow key={`review-payout-${payout.id}`} payout={payout} isLoading={isLoading} currentEmployeeId={currentEmployeeId} onClick={() => onReviewPayout(payout)} />)
                )}
              </TableBody>
            </Table>
          </PaginatedList>
        </Section>
      )}
    </>
  );
};

export default ReviewPayments;
